import { type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface AnimatedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Max width class (e.g. "max-w-4xl", "max-w-md") */
  maxWidth?: string;
  /** Extra classes on the content panel */
  className?: string;
}

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const contentVariants = {
  hidden: { opacity: 0, scale: 0.96, y: 16 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.97, y: 8 },
};

const springTransition = {
  duration: 0.32,
  ease: [0.16, 1, 0.3, 1] as const,
};

/**
 * Wraps Radix Dialog with framer-motion for smooth
 * enter and exit animations on both overlay and content.
 *
 * Usage:
 * ```tsx
 * <AnimatedDialog open={open} onOpenChange={setOpen} maxWidth="max-w-md">
 *   <Dialog.Title>...</Dialog.Title>
 *   ...content...
 * </AnimatedDialog>
 * ```
 */
export function AnimatedDialog({
  open,
  onOpenChange,
  children,
  maxWidth = "max-w-4xl",
  className,
}: AnimatedDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            {/* Animated overlay */}
            <Dialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={{ duration: 0.22 }}
              />
            </Dialog.Overlay>

            {/* Animated content */}
            <Dialog.Content
              asChild
              forceMount
              onInteractOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <motion.div
                className={cn(
                  "fixed left-1/2 top-1/2 z-50 w-[90vw] -translate-x-1/2 -translate-y-1/2",
                  "rounded-xl bg-white shadow-2xl",
                  maxWidth,
                  className
                )}
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={springTransition}
              >
                {children}
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
